import React, { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Search, Loader2, MapPin, Filter, Plus, CheckSquare, Trash2, X, Check } from "lucide-react";
import { api } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { StatusBadge } from "@/components/StatusBadge";
import { Progress } from "@/components/ui/progress";
import { STATUS_META, SERVICES_META } from "@/lib/constants";
import { formatDateShort, maskPhoneBR } from "@/lib/format";
import { toast } from "sonner";

const EMPTY = {
  couple_name: "", date: "", local: "", phone: "", deadline: "",
  services: { cerimonia: true, banda: false, astrolive: false },
  astrolive_products: { sax: false, dj: false, bazuca: false, areia: false, bananinha: false },
};

export default function Clients() {
  const navigate = useNavigate();
  const [sp] = useSearchParams();
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [status, setStatus] = useState(sp.get("status") || "all");
  const [service, setService] = useState(sp.get("service") || "all");
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  // Seleção em massa
  const [selectMode, setSelectMode] = useState(false);
  const [selected, setSelected] = useState(new Set());
  const [confirmBulk, setConfirmBulk] = useState(false);
  // Exclusão adiada (janela de "Desfazer")
  const pendingRef = useRef(null); // { timer, ids, docs }

  // Sincroniza filtros quando a URL muda (atalhos por serviço da navegação)
  useEffect(() => {
    setService(sp.get("service") || "all");
    setStatus(sp.get("status") || "all");
  }, [sp]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (q) params.q = q;
      if (status !== "all") params.status = status;
      if (service !== "all") params.service = service;
      const { data } = await api.get("/admin/events", { params });
      setEvents(data.events || []);
    } catch {
      toast.error("Não foi possível carregar os eventos.");
    } finally { setLoading(false); }
  }, [q, status, service]);

  useEffect(() => { const t = setTimeout(load, 300); return () => clearTimeout(t); }, [load]);

  const createEvent = async () => {
    if (!form.couple_name || !form.date || !form.local || form.phone.replace(/\D/g, "").length < 10) {
      toast.error("Preencha casal, data, local e um contato válido.");
      return;
    }
    setSaving(true);
    try {
      const { data } = await api.post("/admin/events", form);
      toast.success("Evento criado! Compartilhe o link com o casal.");
      setOpen(false);
      setForm(EMPTY);
      navigate(`/admin/clientes/${data.id}`);
    } catch { toast.error("Erro ao criar evento."); } finally { setSaving(false); }
  };

  const toggleSvc = (k) => setForm((f) => ({ ...f, services: { ...f.services, [k]: !f.services[k] } }));
  const toggleProd = (k) => setForm((f) => ({ ...f, astrolive_products: { ...f.astrolive_products, [k]: !f.astrolive_products[k] } }));

  // --- Seleção em massa ---
  const exitSelectMode = () => { setSelectMode(false); setSelected(new Set()); };

  const toggleSelectMode = () => {
    if (selectMode) exitSelectMode();
    else setSelectMode(true);
  };

  const toggleOne = (id) => setSelected((prev) => {
    const next = new Set(prev);
    if (next.has(id)) next.delete(id); else next.add(id);
    return next;
  });

  const allSelected = events.length > 0 && selected.size === events.length;
  const toggleSelectAll = () => {
    setSelected(allSelected ? new Set() : new Set(events.map((e) => e.id)));
  };

  const onCardClick = (id) => {
    if (selectMode) toggleOne(id);
    else navigate(`/admin/clientes/${id}`);
  };

  // Executa a exclusão real no backend
  const flushDeletion = async (ids) => {
    try {
      await api.post("/admin/events/bulk-delete", { ids });
    } catch {
      toast.error("Falha ao excluir no servidor. Recarregando lista.");
      load();
    }
  };

  // Restaura os eventos removidos otimisticamente para a lista (ordenados por data)
  const restoreDocs = (docs) => {
    setEvents((prev) => {
      const known = new Set(prev.map((e) => e.id));
      const merged = [...prev, ...docs.filter((d) => !known.has(d.id))];
      return merged.sort((a, b) => (a.date || "").localeCompare(b.date || ""));
    });
  };

  const confirmBulkDelete = () => {
    // Se houver uma exclusão pendente, conclui a anterior antes de iniciar outra
    if (pendingRef.current) {
      clearTimeout(pendingRef.current.timer);
      flushDeletion(pendingRef.current.ids);
      pendingRef.current = null;
    }

    const ids = Array.from(selected);
    const docs = events.filter((e) => selected.has(e.id));
    if (ids.length === 0) return;

    // Remoção otimista + fecha diálogo/seleção
    setEvents((prev) => prev.filter((e) => !selected.has(e.id)));
    setConfirmBulk(false);
    exitSelectMode();

    const timer = setTimeout(() => {
      flushDeletion(ids);
      pendingRef.current = null;
    }, 6000);
    pendingRef.current = { timer, ids, docs };

    toast(`${ids.length} evento(s) excluído(s).`, {
      duration: 6000,
      action: {
        label: "Desfazer",
        onClick: () => {
          if (!pendingRef.current) return;
          clearTimeout(pendingRef.current.timer);
          const restored = pendingRef.current.docs;
          pendingRef.current = null;
          restoreDocs(restored);
          toast.success("Exclusão desfeita.");
        },
      },
    });
  };

  // Ao sair da página, conclui qualquer exclusão pendente para não perder a intenção
  useEffect(() => {
    return () => {
      if (pendingRef.current) {
        clearTimeout(pendingRef.current.timer);
        flushDeletion(pendingRef.current.ids);
        pendingRef.current = null;
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div>
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="font-serif text-3xl font-semibold text-[color:var(--brand-graphite)]">Clientes</h1>
          <p className="mt-1 text-sm text-[color:var(--brand-muted)]">Pesquise, filtre e acompanhe cada evento.</p>
        </div>
        <Button data-testid="create-event-button" onClick={() => setOpen(true)} className="h-11 bg-[color:var(--brand-marsala)] text-white hover:bg-[color:var(--brand-marsala-hover)]">
          <Plus className="mr-2 h-4 w-4" /> Criar evento
        </Button>
      </div>

      {/* Barra de seleção em massa */}
      <div className="mt-4 flex flex-col gap-3 rounded-xl border border-[color:var(--brand-border)] bg-white p-3 sm:flex-row sm:items-center sm:justify-between">
        {!selectMode ? (
          <>
            <p className="text-sm text-[color:var(--brand-muted)]">Precisa limpar eventos de teste? Selecione vários e exclua de uma vez.</p>
            <Button data-testid="clients-select-toggle" variant="outline" onClick={toggleSelectMode} className="h-10">
              <CheckSquare className="mr-2 h-4 w-4" /> Selecionar
            </Button>
          </>
        ) : (
          <>
            <div className="flex items-center gap-3">
              <button type="button" data-testid="clients-select-all" onClick={toggleSelectAll}
                className="inline-flex items-center gap-2 text-sm font-medium text-[color:var(--brand-graphite)] hover:text-[color:var(--brand-marsala)]">
                <span className={`flex h-4 w-4 items-center justify-center rounded border ${allSelected ? "border-[color:var(--brand-marsala)] bg-[color:var(--brand-marsala)] text-white" : "border-[color:var(--brand-border)]"}`}>
                  {allSelected && <Check className="h-3 w-3" />}
                </span>
                {allSelected ? "Desmarcar todos" : "Selecionar todos"}
              </button>
              <span data-testid="clients-selected-count" className="rounded-full bg-[color:var(--brand-champagne-soft)] px-2.5 py-0.5 text-xs font-semibold text-[color:var(--brand-graphite)]">
                {selected.size} selecionado(s)
              </span>
            </div>
            <div className="flex gap-2">
              <Button data-testid="clients-bulk-delete" onClick={() => setConfirmBulk(true)} disabled={selected.size === 0}
                className="h-10 bg-[color:var(--brand-danger)] text-white hover:bg-[color:var(--brand-danger)]/90 disabled:opacity-50">
                <Trash2 className="mr-2 h-4 w-4" /> Excluir selecionados
              </Button>
              <Button data-testid="clients-select-cancel" variant="outline" onClick={exitSelectMode} className="h-10">
                <X className="mr-2 h-4 w-4" /> Cancelar
              </Button>
            </div>
          </>
        )}
      </div>

      <div className="mt-6 flex flex-col gap-3 sm:flex-row">
        <div className="relative flex-1">
          <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-[color:var(--brand-muted)]" />
          <Input data-testid="clients-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Buscar por nome do casal" className="h-11 pl-9" />
        </div>
        <Select value={service} onValueChange={setService}>
          <SelectTrigger data-testid="clients-service-filter" className="h-11 sm:w-48"><SelectValue placeholder="Serviço" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Todos os serviços</SelectItem>
            <SelectItem value="cerimonia">Cerimônia</SelectItem>
            <SelectItem value="banda">Banda / Receptivo</SelectItem>
            <SelectItem value="astrolive">AstroLive</SelectItem>
          </SelectContent>
        </Select>
        <Select value={status} onValueChange={setStatus}>
          <SelectTrigger data-testid="clients-status-filter" className="h-11 sm:w-52"><Filter className="mr-2 h-4 w-4 text-[color:var(--brand-muted)]" /><SelectValue placeholder="Status" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Todos os status</SelectItem>
            {Object.entries(STATUS_META).map(([k, v]) => <SelectItem key={k} value={k}>{v.label}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>

      <div className="mt-5 space-y-2">
        {loading ? (
          <div className="flex justify-center py-16"><Loader2 className="h-6 w-6 animate-spin text-[color:var(--brand-marsala)]" /></div>
        ) : events.length === 0 ? (
          <div className="rounded-2xl border border-[color:var(--brand-border)] bg-white p-8 text-center text-sm text-[color:var(--brand-muted)]">Nenhum cliente encontrado.</div>
        ) : (
          events.map((e) => {
            const isSel = selected.has(e.id);
            return (
            <button key={e.id} data-testid={`client-row-${e.id}`} onClick={() => onCardClick(e.id)}
              className={`flex w-full flex-col gap-3 rounded-2xl border bg-white p-4 text-left transition-colors sm:flex-row sm:items-center sm:justify-between ${isSel ? "border-[color:var(--brand-marsala)] bg-[color:var(--brand-marsala-soft)]" : "border-[color:var(--brand-border)] hover:bg-[color:var(--brand-paper)]"}`}>
              {selectMode && (
                <span data-testid={`client-check-${e.id}`}
                  className={`flex h-5 w-5 shrink-0 items-center justify-center rounded border ${isSel ? "border-[color:var(--brand-marsala)] bg-[color:var(--brand-marsala)] text-white" : "border-[color:var(--brand-border)] bg-white"}`}>
                  {isSel && <Check className="h-3.5 w-3.5" />}
                </span>
              )}
              <div className="min-w-0 flex-1">
                <div className="flex flex-wrap items-center gap-2">
                  <p className="font-semibold text-[color:var(--brand-graphite)]">{e.couple_name}</p>
                  {!e.viewed_by_admin && e.status === "enviado" && <span className="rounded-full bg-[color:var(--brand-marsala)] px-2 py-0.5 text-[10px] font-semibold text-white">Novo</span>}
                  {Object.keys(SERVICES_META).filter((k) => (e.services || {})[k]).map((k) => (
                    <span key={k} className="rounded bg-[color:var(--brand-champagne-soft)] px-1.5 py-0.5 text-[10px] font-semibold text-[color:var(--brand-graphite)]">{SERVICES_META[k].short}</span>
                  ))}
                </div>
                <p className="mt-0.5 flex items-center gap-3 text-xs text-[color:var(--brand-muted)]">
                  <span className="inline-flex items-center gap-1"><MapPin className="h-3 w-3" /> {e.local}</span>
                  <span>{formatDateShort(e.date)}</span>
                </p>
                <div className="mt-2 flex items-center gap-2"><Progress value={e.progress?.percent || 0} className="h-1.5 w-32" /><span className="text-[11px] text-[color:var(--brand-muted)]">{e.progress?.percent || 0}%</span></div>
              </div>
              <StatusBadge status={e.status} />
            </button>
            );
          })
        )}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-h-[90vh] max-w-lg overflow-y-auto">
          <DialogHeader><DialogTitle className="font-serif text-xl">Criar evento</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label>Nome do casal</Label><Input data-testid="create-couple" value={form.couple_name} onChange={(e) => setForm({ ...form, couple_name: e.target.value })} className="mt-1 h-11" placeholder="Beatriz & Davi" /></div>
            <div className="grid grid-cols-2 gap-2">
              <div><Label>Data</Label><Input data-testid="create-date" type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} className="mt-1 h-11" /></div>
              <div><Label>Prazo (opcional)</Label><Input type="date" value={form.deadline} onChange={(e) => setForm({ ...form, deadline: e.target.value })} className="mt-1 h-11" /></div>
            </div>
            <div><Label>Local</Label><Input data-testid="create-local" value={form.local} onChange={(e) => setForm({ ...form, local: e.target.value })} className="mt-1 h-11" /></div>
            <div><Label>WhatsApp</Label><Input data-testid="create-phone" value={form.phone} onChange={(e) => setForm({ ...form, phone: maskPhoneBR(e.target.value) })} className="mt-1 h-11" placeholder="(21) 99999-9999" /></div>
            <div>
              <Label>Serviços contratados</Label>
              <div className="mt-2 flex flex-wrap gap-2">
                {Object.entries(SERVICES_META).map(([k, meta]) => (
                  <button key={k} type="button" data-testid={`create-svc-${k}`} onClick={() => toggleSvc(k)}
                    className={`rounded-full border px-3 py-1.5 text-sm ${form.services[k] ? "border-[color:var(--brand-marsala)] bg-[color:var(--brand-marsala-soft)] text-[color:var(--brand-marsala)]" : "border-[color:var(--brand-border)] text-[color:var(--brand-graphite)]"}`}>{meta.label}</button>
                ))}
              </div>
            </div>
            {form.services.astrolive && (
              <div>
                <Label>Itens do AstroLive contratados</Label>
                <div className="mt-2 flex flex-wrap gap-2">
                  {[["sax", "Sax"], ["dj", "DJ"], ["bazuca", "Bazuca CO₂"], ["areia", "Areia nos tambores"], ["bananinha", "Bebida alcoólica (bananinha)"]].map(([k, lbl]) => (
                    <button key={k} type="button" data-testid={`create-prod-${k}`} onClick={() => toggleProd(k)}
                      className={`rounded-full border px-3 py-1.5 text-sm ${form.astrolive_products[k] ? "border-[color:var(--brand-marsala)] bg-[color:var(--brand-marsala-soft)] text-[color:var(--brand-marsala)]" : "border-[color:var(--brand-border)] text-[color:var(--brand-graphite)]"}`}>{lbl}</button>
                  ))}
                </div>
                <p className="mt-1 text-[11px] text-[color:var(--brand-muted)]">* Marque os itens do pacote para referência interna. As autorizações (bebida, areia de LED, CO₂) sempre aparecem para o cliente no AstroLive; a bebida é ocultada automaticamente em eventos Gospel.</p>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button data-testid="create-event-save" onClick={createEvent} disabled={saving} className="bg-[color:var(--brand-marsala)] text-white hover:bg-[color:var(--brand-marsala-hover)]">
              {saving ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null} Criar e gerar link
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={confirmBulk} onOpenChange={setConfirmBulk}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Excluir {selected.size} evento(s)?</AlertDialogTitle>
            <AlertDialogDescription>
              Todos os eventos selecionados e seus repertórios serão removidos permanentemente. Esta ação não pode ser desfeita.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction data-testid="clients-bulk-delete-confirm" onClick={(e) => { e.preventDefault(); confirmBulkDelete(); }}
              className="bg-[color:var(--brand-danger)] text-white hover:bg-[color:var(--brand-danger)]/90">
              <Trash2 className="mr-2 h-4 w-4" /> Excluir
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
